import React, { useState, useEffect } from 'react'
import Popup from '../../../components/Popup'
import notifyUser from "../../../methods/notifyUser";
import {connect} from "react-redux";
import ApiKeyField from "../../../components/ApiKeyField";
import { Autocomplete } from '@mui/material';
import TextField from "../../../components/elements/TextField";
import { httpClient } from '../../../funcs';

const DOC_URL = 'https://docs.google.com/document/d/1LaTvQjajJ8ghLwZnuRQPeCJgKcLsPete83vwoPhQbDk/edit?tab=t.0'
const SWAGGER_URL = `${process.env.REACT_APP_API_URL || ''}/docs`

// Получаем список всех доступных таймзон из Intl API
const getAllTimezones = (browserTimezone: string) => {
    try {
        return Intl.supportedValuesOf('timeZone').map(tz => {
            // Получаем смещение для таймзоны
            const now = new Date()
            const offset = new Intl.DateTimeFormat('en', {
                timeZone: tz,
                timeZoneName: 'longOffset'
            }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || ''
            
            // Форматируем название для отображения
            const cityName = tz.split('/').pop()?.replace(/_/g, ' ') || tz
            const region = tz.split('/')[0]?.replace(/_/g, ' ') || ''
            
            return {
                value: tz,
                label: `${cityName} ${offset}`,
                region: region,
                fullLabel: `${cityName} (${region}) ${offset}`,
                isBrowser: tz === browserTimezone
            }
        }).sort((a, b) => {
            // Сначала таймзона браузера, потом остальные
            if (a.isBrowser && !b.isBrowser) return -1
            if (!a.isBrowser && b.isBrowser) return 1
            
            // Затем сортируем по региону, потом по городу
            if (a.region !== b.region) {
                return a.region.localeCompare(b.region)
            }
            return a.value.localeCompare(b.value)
        })
    } catch (error) {
        console.warn('Intl.supportedValuesOf не поддерживается, используем fallback список')
        // Fallback список для старых браузеров
        return [
            { value: 'UTC', label: 'UTC (Всемирное координированное время)', region: 'UTC', fullLabel: 'UTC (Всемирное координированное время)', isBrowser: false },
            { value: 'Europe/Moscow', label: 'Москва UTC+3', region: 'Europe', fullLabel: 'Москва (Europe) UTC+3', isBrowser: false },
            { value: 'Europe/Kiev', label: 'Киев UTC+2', region: 'Europe', fullLabel: 'Киев (Europe) UTC+2', isBrowser: false },
            { value: 'Europe/Minsk', label: 'Минск UTC+3', region: 'Europe', fullLabel: 'Минск (Europe) UTC+3', isBrowser: false },
            { value: 'Europe/London', label: 'Лондон UTC+0/+1', region: 'Europe', fullLabel: 'Лондон (Europe) UTC+0/+1', isBrowser: false },
            { value: 'Europe/Berlin', label: 'Берлин UTC+1/+2', region: 'Europe', fullLabel: 'Берлин (Europe) UTC+1/+2', isBrowser: false },
            { value: 'Europe/Paris', label: 'Париж UTC+1/+2', region: 'Europe', fullLabel: 'Париж (Europe) UTC+1/+2', isBrowser: false },
            { value: 'America/New_York', label: 'Нью-Йорк UTC-5/-4', region: 'America', fullLabel: 'Нью-Йорк (America) UTC-5/-4', isBrowser: false },
            { value: 'America/Los_Angeles', label: 'Лос-Анджелес UTC-8/-7', region: 'America', fullLabel: 'Лос-Анджелес (America) UTC-8/-7', isBrowser: false },
            { value: 'Asia/Tokyo', label: 'Токио UTC+9', region: 'Asia', fullLabel: 'Токио (Asia) UTC+9', isBrowser: false },
            { value: 'Asia/Shanghai', label: 'Шанхай UTC+8', region: 'Asia', fullLabel: 'Шанхай (Asia) UTC+8', isBrowser: false },
            { value: 'Asia/Dubai', label: 'Дубай UTC+4', region: 'Asia', fullLabel: 'Дубай (Asia) UTC+4', isBrowser: false },
            { value: 'Australia/Sydney', label: 'Сидней UTC+10/+11', region: 'Australia', fullLabel: 'Сидней (Australia) UTC+10/+11', isBrowser: false },
        ]
    }
}

// Получаем таймзону браузера
const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        console.warn('Не удалось определить таймзону браузера')
        return 'UTC'
    }
}


type Props = {
    close: Function,
    $store: any,
    $commitToStore: Function,
}

let ApiIntegrationPopup: any = function ApiIntegrationPopup(props: Props) {
    const [selectedTimezone, setSelectedTimezone] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [timezones, setTimezones] = useState<Array<{value: string, label: string, region: string, fullLabel: string, isBrowser: boolean}>>([])
    const [isLoadingTimezones, setIsLoadingTimezones] = useState(true)
    const [isEditing, setIsEditing] = useState(false)

    useEffect(() => {
        // Загружаем список таймзон
        const loadTimezones = () => {
            try {
                const browserTimezone = getBrowserTimezone()
                const allTimezones = getAllTimezones(browserTimezone)
                setTimezones(allTimezones)
                setIsLoadingTimezones(false)
            } catch (error) {
                console.error('Ошибка загрузки таймзон:', error)
                setIsLoadingTimezones(false)
            }
        }
        
        loadTimezones()
    }, [])

    useEffect(() => {
        // Загружаем текущую таймзону пользователя
        const currentTimezone = props.$store.userSession?.settings?.time_zone
        if (currentTimezone) {
            setSelectedTimezone(currentTimezone)
        } else {
            // Если у пользователя нет сохраненной таймзоны, оставляем поле пустым
            setSelectedTimezone('')
        }
    }, [props.$store.userSession])

    function onCopy() {
        notifyUser(props, "API ключ скопирован в буфер обмена");
    }

    function apiKey() {
        return `${props.$store.userSession.realty_api_key}`
    }

    async function saveTimezone(timezone: string = selectedTimezone) {
        if (isSaving) return
        
        setIsSaving(true)
        try {
            const response = await httpClient.post('/users/timezone', {
                time_zone: timezone
            })

            // Обновляем состояние пользователя
            props.$commitToStore({
                type: 'UPDATE_USER_SETTINGS',
                settings: { time_zone: timezone }
            })
            notifyUser(props, "Настройка таймзоны сохранена")
        } catch (error) {
            console.error('Ошибка при сохранении таймзоны:', error)
            notifyUser(props, "Ошибка при сохранении настройки", 'error')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup maxWidth="650px" onClose={() => {
            props.close()
        }}>
            <div>
                <div className="commonTitle" style={{marginBottom: 16}}>
                    Прямая интеграция по API
                </div>
                <div className="commonSubtitle" style={{marginBottom: 24}}>
                    Используйте API для интеграции вашей системы с ОкиДоки напрямую. Вся документация доступна по кнопке ниже.
                </div>
                
                <div className="commonSubtitle" style={{marginBottom: 24}}>
                    В документации описана схема интеграции и все необходимые методы для работы с API.
                </div>

                <div style={{display: 'flex', gap: '12px', marginBottom: 24, flexWrap: 'wrap'}}>
                    <a
                        href={DOC_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="new withTitle _wa"
                        style={{display: 'inline-block', flex: '1', minWidth: '200px'}}
                    >
                        Открыть документацию
                    </a>
                    <a
                        href={SWAGGER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="new withTitle _wa"
                        style={{display: 'inline-block', flex: '1', minWidth: '200px'}}
                    >
                        Swagger API
                    </a>
                </div>

                <hr className="newHR"/>
                <div className="Body_15" style={{textAlign: "left"}}>
                    Для работы с API используйте ваш персональный ключ:
                </div>
                <div>
                    <ApiKeyField
                        title="API ключ"
                        value={apiKey()}
                        onCopy={onCopy}
                    />
                </div>

                <hr className="newHR" style={{marginBottom: 24}} />

                <div className="commonLabel" style={{marginBottom: 16}}>
                    Настройки отображения дат
                </div>
                <div className="commonSubtitle" style={{marginBottom: 16}}>
                    Выберите таймзону для корректного отображения дат в документах:
                </div>
                
                <div style={{marginBottom: 16}}>
                    {isLoadingTimezones ? (
                        <div style={{padding: '8px 12px', color: '#666', fontSize: '14px'}}>
                            Загрузка списка таймзон...
                        </div>
                    ) : (
                        <Autocomplete
                            freeSolo
                            handleHomeEndKeys={true}
                            className="classicStyleMuiSelect"
                            value={timezones.find(tz => tz.value === selectedTimezone) || null}
                            options={timezones}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option
                                return option?.fullLabel || ''
                            }}
                            filterOptions={(options, { inputValue }) => {
                                const filtered = options.filter(option => 
                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.fullLabel.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                    option.region.toLowerCase().includes(inputValue.toLowerCase())
                                )
                                return filtered
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <span style={{ fontWeight: 500, fontSize: '16px' }}>{option.label}</span>
                                            {option.isBrowser && (
                                                <span style={{ 
                                                    marginLeft: '8px', 
                                                    fontSize: '12px', 
                                                    color: '#75809E',
                                                    backgroundColor: '#F5F6F9',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px'
                                                }}>
                                                    🌐 Браузер
                                                </span>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                            {option.region}
                                        </span>
                                    </div>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={
                                        isSaving 
                                            ? "Сохранение..." 
                                            : selectedTimezone 
                                                ? "Выберите таймзону" 
                                                : "Начните вводить название города или региона для выбора таймзоны..."
                                    }
                                    disabled={isSaving}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            border: isEditing ? "1px solid #75809E !important" : selectedTimezone ? "none" : "1px solid #ff9800 !important",
                                            background: isEditing ? "white !important" : selectedTimezone ? "#EAECF3" : "#fff3e0",
                                            padding: isEditing ? "1.5px !important" : "2.5px",
                                            height: "60px",
                                            opacity: isSaving ? 0.7 : 1,
                                        }
                                    }}
                                    variant="outlined"
                                />
                            )}
                            onFocus={() => setIsEditing(true)}
                            onBlur={() => setIsEditing(false)}
                            onChange={(event, value) => {
                                if (value && typeof value === 'object') {
                                    setSelectedTimezone(value.value)
                                    // Автосохранение при изменении таймзоны
                                    saveTimezone(value.value)
                                }
                            }}
                        />
                    )}
                </div>
                
                <div className="commonSubtitle" style={{marginBottom: 16, fontSize: '12px', color: selectedTimezone ? '#666' : '#ff9800'}}>
                    {selectedTimezone 
                        ? `✅ Выбрана таймзона: ${timezones.find(tz => tz.value === selectedTimezone)?.fullLabel || selectedTimezone}. Настройка сохраняется автоматически.`
                        : `⚠️ Рекомендуется выбрать таймзону для корректного отображения дат. Таймзона вашего браузера (${getBrowserTimezone()}) будет показана первой в списке.`
                    }
                </div>


                <hr className="newHR" style={{marginBottom: 24}} />

                <div className="commonLabel" style={{marginBottom: 16}}>
                    Нужна помощь с интеграцией?
                </div>
                <div className="commonSubtitle" style={{marginBottom: 16}}>
                    Обратитесь к нашей технической поддержке:
                </div>
                
                <div style={{marginBottom: 16}}>
                    <div className="commonSubtitle" style={{marginBottom: 8}}>
                        <strong>Telegram:</strong> <a href="https://t.me/superpupermegaman" target="_blank" rel="noopener noreferrer" style={{color: '#1FA3EE', textDecoration: 'none'}}>@superpupermegaman</a>
                    </div>
                    <div className="commonSubtitle" style={{marginBottom: 16}}>
                        <strong>Телефон:</strong> <a href="tel:+79397163709" style={{color: '#1FA3EE', textDecoration: 'none'}}>+7 (939) 716-37-09</a>
                    </div>
                </div>

                <button
                    className="new withTitle _wa"
                    onClick={() => {
                        props.close()
                    }}
                >
                    <div className="buttonsContainer">
                        <span>Закрыть</span>
                    </div>
                </button>
            </div>
        </Popup>
    )
}

ApiIntegrationPopup = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(ApiIntegrationPopup)

export default ApiIntegrationPopup 